/**
 * Terra Anima (테라 아니마) - Core Type Definitions
 */

export type Direction8 = 'N' | 'NE' | 'E' | 'SE' | 'S' | 'SW' | 'W' | 'NW';

export type { ZoneTileType } from '../modules/world/ZoneMapData';

export type DamageType = 'slash' | 'thrust' | 'crush' | 'holy' | 'pure';

export type EntityRole =
  | 'tank'
  | 'ranged_dps'
  | 'caster_dps'
  | 'healer'
  | 'buffer'
  | 'monk_hybrid'
  | 'monster'
  | 'npc'
  | 'resource_node'
  | 'admin';

export type EntityCategory = 'player' | 'companion' | 'monster' | 'npc' | 'resource';

export type RaceId =
  | 'solarian'
  | 'borean'
  | 'sylvanian'
  | 'ignian'
  | 'lumian'
  | 'nebulan';

export type GenderType = 'male' | 'female';
export type BodyShapeType = 'slim' | 'normal' | 'muscular' | 'heavy';
export type EyeAngleType = 'normal' | 'sharp' | 'droopy';
export type EyeSizeType = 'small' | 'medium' | 'large';

export type HairStyleId =
  | 'short'
  | 'long'
  | 'ponytail'
  | 'mohawk'
  | 'tonsure'
  | 'braids'
  | 'twin_tail'
  | 'bob_cut'
  | 'wavy_long'
  | 'pixie';

export type FacialFeatureId = 'none' | 'beard' | 'mustache' | 'scar' | 'tattoo';

export interface CharacterAppearance {
  gender?: GenderType;
  skinColor: string;
  hairStyle: HairStyleId;
  hairColor: string;
  facialFeature: FacialFeatureId;
  eyeColor: string;
  eyeAngle?: EyeAngleType;
  eyeSize?: EyeSizeType;
  bodyShape?: BodyShapeType;
  primaryArmorColor: string;
  secondaryArmorColor: string;
  capeColor: string;
  showHelm: boolean;
  bodyScale: number; // 0.88 ~ 1.15
  titlePrefix?: string;
  guild?: string; // 소속 길드명 (기본: <솔라리스 초보자길드>)
  isKing?: boolean; // Lord British King model (Crown + Royal Scepter + Ermine Robes)
}

export interface PersonalityAxes {
  order: number;        // -100 (혼돈 Chaotic) ↔ +100 (질서 Lawful)
  moral: number;        // -100 (악 Evil) ↔ +100 (선 Good)
  courage: number;      // -100 (겁 Fearful) ↔ +100 (용기 Brave)
  materialism: number;  // -100 (금욕 Ascetic) ↔ +100 (물질욕 Greedy)
  extraversion: number; // -100 (내향 I) ↔ +100 (외향 E)
  intuition: number;    // -100 (감각 S) ↔ +100 (직관 N)
  thinking: number;     // -100 (감정 F) ↔ +100 (사고 T)
  perceiving: number;   // -100 (판단 J) ↔ +100 (인식 P)
}

export interface PersonalityProfile {
  axes: PersonalityAxes;
  alignment: string;     // e.g. '질서 선 (Lawful Good)'
  mbti: string;          // e.g. 'ENFJ'
  soulTitle: string;     // e.g. '새벽을 밝히는 수호 기사'
  summary: string;       // Narrative explanation
}

export interface StatBlock {
  str: number; // 힘: 슬래시/크러시 데미지 및 치명타율, 소지 무게
  con: number; // 체력: HP와 SP 양 결정
  dex: number; // 민첩: 회피(블럭/패리/이베이드)율, 쓰러스트 데미지 및 치명타율
  qui: number; // 속도: 무기 휘두르는 속도 + 주문 시전 속도
  int: number; // 지능: 마법사/레인저 MP양 및 최대 집중력
  pie: number; // 신앙: 클레릭/수도승/팔라딘 MP양 및 최대 집중력
  cha: number; // 매력: 바드 MP양 및 최대 집중력, 메즈 관통, 흥정
}

export interface SelfSkillBlock {
  // a. 전투스킬 (0.0 ~ 100.0)
  slash: number;
  thrust: number;
  crush: number;
  // b. 연습스킬 (0.0 ~ 100.0)
  experiment: number;  // 실험 (마법사)
  prayer: number;      // 기도 (클레릭/수도승/팔라딘)
  training: number;    // 훈련 (근/원거리 무기)
  performance: number; // 연주 (바드)
  // c. 생활스킬 (0.0 ~ 100.0)
  woodworking: number;
  metalworking: number;
  leatherworking: number;
  tailoring: number;
  alchemy: number;
  farming: number;
  husbandry: number;
  cooking: number;
  // d. 공통스킬 (0.0 ~ 100.0)
  rhetoric: number;    // 화술
  busking: number;     // 공연
  begging: number;     // 구걸
  stealing: number;    // 훔치기
}

export interface CraftedComponent {
  id: string;
  name: string;
  partType: 'primary' | 'secondary1' | 'secondary2';
  skillUsed: keyof SelfSkillBlock;
  quality: number; // 60 ~ 100%
}

export interface EnchantBonus {
  type: 'skill' | 'stat';
  targetKey: string;   // skill slot (e.g. 'slot_2') or stat key ('str')
  targetName: string;  // e.g. '타운트', '힐링챈트', '힘'
  value: number;       // e.g. +2, +3
}

export interface CraftedEquipment {
  id: string;
  name: string;
  category: 'plate_shield' | 'sword_axe' | 'mace_staff' | 'bow_leather' | 'robe_cloth';
  slot: 'weapon' | 'offhand' | 'armor';
  itemLevel: number;
  quality: number;           // 60 ~ 100%
  components: {
    primary: CraftedComponent;    // 60% weight -> 기본 공격력/방어력
    secondary1: CraftedComponent; // 25% weight -> 공속/시전보정 & SP소모 절감
    secondary2: CraftedComponent; // 15% weight -> 내구도 & 무게 절감
  };
  attackPowerMod: number;    // From primary quality
  speedSpMod: number;        // From secondary1 quality (e.g. -0.15 SP cost, +0.10 speed)
  durabilityMod: number;     // From secondary2 quality
  weightKg: number;
  enchants: EnchantBonus[];
}

export interface Vector2D {
  x: number;
  y: number;
}

export interface ResourcePool {
  hp: number;
  maxHp: number;
  sp: number;
  maxSp: number;
  mp: number;
  maxMp: number;
  conc: number;       // Remaining available Concentration
  maxConc: number;    // Dynamic max Concentration (100 + Primary Caster Stat bonus)
}

export interface ActiveBuff {
  id: string;
  name: string;
  sourceId: string;
  concCost: number;     // Concentration points reserved by this buff
  duration: number;     // Duration in seconds (-1 for indefinite toggle)
  elapsed: number;
  damageReduction?: number;
  spRegenBonus?: number;
  mpRegenBonus?: number;
  hpRegenBonus?: number;
  moveSpeedBonus?: number;
  ignoreHpSpeedPenalty?: boolean; // True for Bard SOS
  attackSpeedBonus?: number;
  castSpeedBonus?: number;
  evadeBonus?: number;
  critBonus?: number;
  manaShieldRatio?: number;
  statBonuses?: Partial<StatBlock>; // DAoC Base Buff (Monk) or Spec Buff (Cleric)
  buffCategory?: 'base_single' | 'spec_composite' | 'self_toggle' | 'pull_def' | 'chant';
  icon: string;
}

export interface ActiveCC {
  type: 'stun' | 'mez' | 'root' | 'snare';
  duration: number;
  remaining: number;
  snarePct?: number; // e.g. 0.40 for 40% slow
}

export interface CastInfo {
  skillId: number;
  skillName: string;
  targetId: string | null;
  castTime: number;      // Total cast time in seconds
  progress: number;      // Progress in seconds (0.0 -> castTime)
  damageType?: DamageType;
  castLevel?: number;
}

export const INVENTORY_CAPACITY = 50;
export const INVENTORY_BASE_UNLOCKED = 20;
export const INVENTORY_UNLOCK_STEP = 5;
export const INVENTORY_UNLOCK_DIAMOND_COST = 50; // 5칸당 50다이아

export type ItemRarity = 'common' | 'magic' | 'fine' | 'masterwork' | 'rare' | 'heroic' | 'legendary';

export const RARITY_TIERS: Record<string, number> = {
  common: 1,      // 일반 (회색)
  magic: 2,       // 마법 (파란색)
  fine: 2,        // (레거시 fine 호환 -> 마법 등급 동등)
  rare: 3,        // 희귀 (노란색)
  masterwork: 3,  // (레거시 masterwork 호환 -> 희귀 등급 동등)
  heroic: 4,      // 영웅 (보라색)
  legendary: 5,   // 전설 (황금색)
};

export interface InventoryItem {
  id: string;
  name: string;
  icon: string;
  category: 'weapon' | 'offhand' | 'armor' | 'material' | 'consumable' | 'quest' | 'mount_gear';
  rarity: ItemRarity;
  quantity: number;
  maxStack: number;
  weightKg: number;
  valueGold: number;                // Item price in total Copper units (1,000C = 1S, 1,000S = 1G, 1,000G = 1P)
  valueCopper?: number;             // Explicit alias for total copper
  description: string;
  craftedEquip?: CraftedEquipment;
  routeTargetStableId?: string;     // Target stable ID if this item is a Horse Route Ticket (승마표)
  isStolen?: boolean;               // True if acquired via pickpocketing/stealing (도둑질 장물)
}

export interface EntityState {
  id: string;
  name: string;
  category: EntityCategory;
  role: EntityRole;
  job: string;
  race?: RaceId;
  appearance?: CharacterAppearance;
  personality?: PersonalityProfile;
  isMounted?: boolean;              // Horseback riding state
  horseColor?: string;              // Coat color of the character's steed
  gold?: number;                    // Total currency carried in Copper units (1,000C = 1S, 1,000S = 1G, 1,000G = 1P)
  fame?: number;                    // 명성 수치 (Fame earned from Quests, Elite & Boss kills; reduced by Stealing)
  theftCount?: number;              // 도둑질 횟수 (NPC 및 동료 가방 절도 기록)
  lootFilterRarity?: 'common' | 'magic' | 'rare' | 'heroic' | 'legendary'; // 파티장 인벤 전리품 최소 등급 필터
  inventory?: Array<InventoryItem | null>; // 50-slot inventory for Characters & NPCs
  unlockedSlots?: number;           // Unlocked inventory slots (base 20, up to 50 via diamond unlock)
  diamonds?: number;                // 다이아 (보석)
  level?: number;                   // 1 ~ 50
  exp?: number;                     // Current EXP (경험치)
  maxExp?: number;                  // EXP required for next level (레벨업 요구 경험치)
  specPointsAvailable?: number;     // Remaining DAoC spec points
  specPointsTotal?: number;
  baseStats?: StatBlock;            // Race base + 30 bonus allocation + life/practice gains
  allocatedBonusStats?: StatBlock;  // Points added at creation (0 ~ 18)
  effectiveStats?: StatBlock;       // Base + Monk Base Buffs + Cleric Spec Buffs + Equipment
  selfSkills?: SelfSkillBlock;      // UO 0.0% ~ 100.0% skills
  specSkillLevels?: Record<number, number>;    // Trained skill level (1 ~ 50) per slot (1..8)
  selectedCastLevels?: Record<number, number>; // Down-ranking selected level (1 ~ trained)
  equipSkillBonuses?: Record<number, number>;  // Enchanter +Skill bonus (capped at +10)
  equipment?: {
    weapon?: CraftedEquipment | null;
    offhand?: CraftedEquipment | null;
    armor?: CraftedEquipment | null;
  };
  armorDebuffPct?: number;          // From Ranger's Mark
  dotEffect?: {
    damagePerSec: number;
    remaining: number;
    tickAccum: number;
    sourceId: string;
    sourceName: string;
  } | null;
  x: number;             // Continuous tile coordinate X
  y: number;             // Continuous tile coordinate Y
  vx: number;
  vy: number;
  dir: Direction8;
  speed: number;
  resources: ResourcePool;
  buffs: ActiveBuff[];
  cc: ActiveCC | null;
  cast: CastInfo | null;
  isDead: boolean;
  color: string;
  weaponType: DamageType;
  cooldowns: Record<number, number>; // slot -> remaining CD in seconds
  threatTable?: Record<string, number>; // targetId -> threat value (for monsters)
  targetId: string | null;
  inCombat: boolean;
  avatarIcon: string;
  resourceType?: 'tree' | 'rock_ore' | 'herb_plant' | 'campfire';
  resourceYield?: { name: string; amount: number };
  respawnRemaining?: number;
  respawnTotal?: number;
  growAnimTimer?: number;
  attackAnimTimer?: number;
}

export interface CombatHitEventData {
  attackerId: string;
  attackerName: string;
  targetId: string;
  targetName: string;
  damage: number;
  damageType: DamageType;
  isCrit: boolean;
  isFriendlyFire: boolean;
}

export interface ThreatChangeEventData {
  monsterId: string;
  targetId: string;
  newThreat: number;
}

export interface CastInterruptedEventData {
  entityId: string;
  entityName: string;
  spellName: string;
  reason: 'damage' | 'movement';
}

export interface LogMessage {
  id: string;
  timestamp: number;
  text: string;
  category:
    | 'combat'
    | 'heal'
    | 'threat'
    | 'system'
    | 'interrupted'
    | 'crafting'
    | 'chat_all'
    | 'chat_normal'
    | 'chat_party'
    | 'chat_guild'
    | 'chat_broadcast'
    | 'chat_world'
    | 'chat_whisper';
}
